import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { Loader2, Pencil, Printer, FileSpreadsheet } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import { api, errMsg } from "@/lib/api";
import { extractFields, withSystem } from "@/lib/card";
import RecordForm from "@/components/data/RecordForm";
import RecordsTable from "@/components/data/RecordsTable";
import ImportDialog from "@/components/data/ImportDialog";
import PreviewPane from "@/components/data/PreviewPane";

export default function DataPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [tpl, setTpl] = useState(null);
  const [records, setRecords] = useState([]);
  const [form, setForm] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [importRows, setImportRows] = useState(null);
  const fileRef = useRef(null);

  const loadRecords = useCallback(() => api.get(`/templates/${id}/records`).then((r) => setRecords(r.data)), [id]);
  useEffect(() => {
    api.get(`/templates/${id}`).then((r) => setTpl(r.data)).catch(() => nav("/"));
    loadRecords();
  }, [id, nav, loadRecords]);

  const fields = useMemo(() => (tpl ? extractFields(tpl) : []), [tpl]);

  const saveRecord = async () => {
    try {
      if (editingId) await api.put(`/records/${editingId}`, { data: form });
      else await api.post(`/templates/${id}/records`, { data: form });
      toast.success(editingId ? "Record updated" : "Card added");
      setForm({});
      setEditingId(null);
      loadRecords();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const onImportFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    try {
      const wb = XLSX.read(await f.arrayBuffer(), { raw: /\.csv$/i.test(f.name) });
      const rows = XLSX.utils.sheet_to_json(wb.Sheets[wb.SheetNames[0]], { defval: "", raw: false });
      if (!rows.length) return toast.error("No rows found in file");
      setImportRows(rows);
    } catch (err) { toast.error("Could not read file"); }
  };

  const downloadSample = () => {
    const ws = XLSX.utils.aoa_to_sheet([fields.map((f) => f.key)]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Cards");
    XLSX.writeFile(wb, `${tpl.name.replace(/\W+/g, "_")}_template.csv`, { bookType: "csv" });
  };

  if (!tpl) return <div className="h-screen grid place-items-center"><Loader2 className="animate-spin text-amber-400" /></div>;

  return (
    <div className="min-h-screen bg-slate-950">
      <AppHeader>
        <span className="text-xs text-slate-500 font-mono hidden md:block">/ {tpl.name} / data</span>
      </AppHeader>
      <main className="max-w-[1500px] mx-auto px-6 lg:px-10 py-8 space-y-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[11px] font-mono text-amber-400 uppercase tracking-[0.2em]">Card holder data</p>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight mt-1" data-testid="data-page-title">{tpl.name}</h1>
            <p className="text-sm text-slate-400 mt-1">Fill the form for one card at a time, or import a CSV / Excel sheet for batch printing.</p>
          </div>
          <div className="flex gap-2">
            <button className="cf-btn-ghost h-9 text-xs" onClick={() => nav(`/editor/${id}`)} data-testid="data-edit-design-button"><Pencil size={14} /> Edit design</button>
            <button className="cf-btn-ghost h-9 text-xs" onClick={() => fileRef.current?.click()} data-testid="bulk-import-button"><FileSpreadsheet size={14} /> Import CSV / Excel</button>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onImportFile} data-testid="bulk-import-dropzone" />
            <button className="cf-btn-primary h-9 text-xs px-4" onClick={() => nav(`/print/${id}`)} data-testid="data-print-button"><Printer size={14} /> Print {records.length} cards</button>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[380px_1fr]">
          <RecordForm fields={fields} form={form} setForm={setForm} editing={!!editingId} onSave={saveRecord}
            onCancel={() => { setForm({}); setEditingId(null); }} onSample={downloadSample} />
          <PreviewPane tpl={tpl} data={withSystem({ id: editingId || "preview", data: form })} />
        </div>

        <RecordsTable tpl={tpl} fields={fields} records={records} reload={loadRecords} editingId={editingId}
          onEdit={(r) => { setEditingId(r.id); setForm(r.data); window.scrollTo({ top: 0, behavior: "smooth" }); }} />
      </main>

      <ImportDialog rows={importRows} fields={fields}
        onImport={(records) => api.post(`/templates/${id}/records/bulk`, { records }).then((r) => r.data.inserted)} onClose={() => setImportRows(null)} onDone={() => { setImportRows(null); loadRecords(); }} />
    </div>
  );
}
